import React, { useState, useEffect } from 'react';
import { HeaderNavComponent } from './header-nav-compontent';
import { getLatestEvents, LatestEvent } from '../api/thunks/financials';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { SignUpPopup } from './signup-popup';
import { formatFiscalDate, getStatusBadge, getEventTypeColor } from '../utils/utils';

interface EventsPageProps {
    // Add props if needed for routing/navigation
}

export const EventsPage: React.FC<EventsPageProps> = () => {
    document.title = "PocketQuant | Latest Events"
    const [events, setEvents] = useState<LatestEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalEvents, setTotalEvents] = useState(0);
    const [selectedEventType, setSelectedEventType] = useState<string>('all');
    const [searchTicker, setSearchTicker] = useState<string>('');
    const [isSignUpPopupOpen, setIsSignUpPopupOpen] = useState(false);
    
    const eventsPerPage = 20;
    const totalPages = Math.ceil(totalEvents / eventsPerPage);

    // Fetch events based on current filters and pagination
    useEffect(() => {
        fetchEvents();
    }, [currentPage, selectedEventType, searchTicker]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [selectedEventType, searchTicker]);

    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            // Calculate offset for pagination
            const offset = (currentPage - 1) * eventsPerPage;
            const limit = eventsPerPage;
            
            const response = await getLatestEvents(limit, offset, selectedEventType, searchTicker);
            
            setEvents(response.events);
            setTotalEvents(response.total);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleEventCardClick = () => {
        setIsSignUpPopupOpen(true);
    };

    const closeSignUpPopup = () => {
        setIsSignUpPopupOpen(false);
    };

    return (
        <div className="events-workspace min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
            <HeaderNavComponent />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl mb-4">
                        Recent Market Events
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Stay up-to-date with the latest earnings calls, SEC filings, and market-moving events across all companies.
                    </p>
                </div>

                {/* Filters Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Search Box */}
                        <div>
                            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                                Search by Ticker
                            </label>
                            <input
                                type="text"
                                id="search"
                                placeholder="e.g., AAPL, NVDA, AMD..."
                                value={searchTicker}
                                onChange={(e) => setSearchTicker(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        {/* Event Type Filter */}
                        <div>
                            <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-2">
                                Event Type
                            </label>
                            <select
                                id="eventType"
                                value={selectedEventType}
                                onChange={(e) => setSelectedEventType(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                                <option value="all">All Events</option>
                                <option value="transcript">Earnings Calls</option>
                                <option value="earnings_report">Earnings Report</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                <div className="flex justify-between items-center mb-6">
                    <p className="text-gray-600">
                        {isLoading ? 'Loading...' : `Showing ${events.length} of ${totalEvents} events`}
                    </p>
                    <p className="text-sm text-gray-500">
                        Page {currentPage} of {totalPages}
                    </p>
                </div>

                {/* Events Grid */}
                {isLoading ? (
                    // Loading State
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {Array.from({ length: 12 }).map((_, index) => (
                            <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    // No Results State
                    <div className="text-center py-12">
                        <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
                            <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0-1.125-.504-1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
                        <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
                    </div>
                ) : (
                    // Events Grid
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {events.map((event) => {
                            const badge = getStatusBadge(event.eventType, event.isTranscript);
                            const eventTypeColor = getEventTypeColor(event.eventType);
                            
                            return (
                                <div 
                                    key={event.id} 
                                    className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-orange-300 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                                    onClick={handleEventCardClick}
                                >
                                    {/* Company Header */}
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                                            <img 
                                                src={`/company_icons/${event.ticker}.png`} 
                                                alt={`${event.companyName} logo`} 
                                                className="w-full h-full object-contain"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    target.parentElement!.innerHTML = `<span class="text-sm font-semibold text-gray-600">${event.ticker}</span>`;
                                                }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 truncate">{event.companyName}</h3>
                                            <p className="text-sm text-gray-600">{event.ticker}</p>
                                        </div>
                                    </div>

                                    {/* Event Details */}
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex justify-between items-start mb-2">
                                                <span className={`text-sm font-medium ${eventTypeColor}`}>
                                                    {event.eventType.replace('_', ' ').toUpperCase()}
                                                </span>
                                                <span className={`px-2 py-1 ${badge.className} text-xs font-medium rounded-full border`}>
                                                    {badge.text}
                                                </span>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-900">{event.quarter}</p>
                                        </div>
                                        
                                        <div className="pt-3 border-t border-gray-100">
                                            <p className="text-xs text-gray-500 mb-1">Fiscal Date Ending</p>
                                            <p className="text-sm font-medium text-gray-700">
                                                {formatFiscalDate(event.fiscalDateEnding)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {!isLoading && events.length > 0 && totalPages > 1 && (
                    <div className="mt-12 flex items-center justify-between bg-white rounded-xl p-6 border border-gray-200">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                                Previous
                            </button>
                            
                            <div className="hidden sm:flex items-center space-x-1">
                                {(() => {
                                    const pageNumbers = [];
                                    const maxVisiblePages = 5;
                                    
                                    if (totalPages <= maxVisiblePages) {
                                        // Show all pages if total is small
                                        for (let i = 1; i <= totalPages; i++) {
                                            pageNumbers.push(i);
                                        }
                                    } else {
                                        // Calculate start and end of window around current page
                                        let start = Math.max(1, currentPage - 2);
                                        let end = Math.min(totalPages, currentPage + 2);
                                        
                                        // Adjust if we're near the beginning or end
                                        if (currentPage <= 3) {
                                            start = 1;
                                            end = maxVisiblePages;
                                        } else if (currentPage >= totalPages - 2) {
                                            start = totalPages - maxVisiblePages + 1;
                                            end = totalPages;
                                        }
                                        
                                        // Add first page and ellipsis if needed
                                        if (start > 1) {
                                            pageNumbers.push(1);
                                            if (start > 2) {
                                                pageNumbers.push('...');
                                            }
                                        }
                                        
                                        // Add the window of pages around current page
                                        for (let i = start; i <= end; i++) {
                                            pageNumbers.push(i);
                                        }
                                        
                                        // Add ellipsis and last page if needed
                                        if (end < totalPages) {
                                            if (end < totalPages - 1) {
                                                pageNumbers.push('...');
                                            }
                                            pageNumbers.push(totalPages);
                                        }
                                    }
                                    
                                    return pageNumbers.map((page, index) => {
                                        if (page === '...') {
                                            return (
                                                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                                    ...
                                                </span>
                                            );
                                        }
                                        
                                        const isActive = page === currentPage;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page as number)}
                                                className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                                    isActive
                                                        ? 'bg-orange-600 text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    });
                                })()}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <ChevronRightIcon className="h-4 w-4 ml-1" />
                            </button>
                        </div>

                        <div className="text-sm text-gray-700">
                            Page <span className="font-medium">{currentPage}</span> of{' '}
                            <span className="font-medium">{totalPages}</span>
                        </div>
                    </div>
                )}

                {/* Back to Home */}
                <div className="text-center mt-12">
                    <a
                        href="/"
                        className="inline-flex items-center px-6 py-3 border border-orange-300 text-base font-medium rounded-lg text-orange-700 bg-white hover:bg-orange-50 transition-colors"
                    >
                        <ChevronLeftIcon className="h-4 w-4 mr-2" />
                        Back to Home
                    </a>
                </div>
            </div>

            {/* Signup Popup */}
            <SignUpPopup 
                isOpen={isSignUpPopupOpen} 
                closeModal={closeSignUpPopup} 
            />
        </div>
    );
};
